import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Fab,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  GetApp as ExportIcon,
  CheckCircle as ValidatedIcon,
  AccountTree as ProfileIcon,
  School as ConsortiumIcon,
  Group as NetworkIcon,
  Edit as EditIcon,
  FileCopy as DuplicateIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import MainLayout from '../layout/MainLayout';

const ProfileManagementPage = () => {
  const [profileBundles, setProfileBundles] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedUploadMethod, setSelectedUploadMethod] = useState('gold_repository');
  const [validationProgress, setValidationProgress] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [configureDialogOpen, setConfigureDialogOpen] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      // Simulate API call - replace with actual API endpoint
      const response = await fetch('/api/profiles/metadata');
      const data = await response.json();
      
      setProfileBundles(data.profile_bundles || []);
      setCustomTemplates(data.custom_templates || []);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      // Fallback demo data
      setProfileBundles([
        {
          id: 'gold_cardiovascular_v2_1',
          name: 'GOLD Cardiovascular Research Bundle v2.1',
          description: 'Umfassende FHIR-Profile für kardiovaskuläre Forschung mit GOLD-Standard-Konformität',
          medical_domain: 'cardiovascular',
          standard_organization: 'GOLD Consortium',
          version: '2.1.0',
          profile_count: 5,
          usage_count: 23,
          validation_status: 'validated',
          vision_zero_compatible: true,
          tags: ['GOLD', 'cardiovascular', 'research', 'consortium', 'vision-zero'],
          integration_info: {
            argus_compatible: true,
            estimated_coverage: '90-95%',
            network_ready: true
          }
        },
        {
          id: 'gold_oncology_v2_1',
          name: 'GOLD Oncology Research Bundle v2.1',
          description: 'Spezialisierte FHIR-Profile für onkologische Forschung und Krebsregisterdaten',
          medical_domain: 'oncology',
          standard_organization: 'GOLD Consortium',
          version: '2.1.0',
          profile_count: 8,
          usage_count: 15,
          validation_status: 'validated',
          vision_zero_compatible: true,
          tags: ['GOLD', 'oncology', 'cancer', 'research', 'vision-zero'],
          integration_info: {
            argus_compatible: true,
            estimated_coverage: '85-92%',
            network_ready: true
          }
        }
      ]);
      setCustomTemplates([
        {
          id: 'charite_cardiotoxicity_template',
          name: 'Charité Cardiotoxicity Template',
          description: 'Spezialisiertes Template für Krebstherapie-bedingte Kardiotoxizität-Studien',
          created_by: 'Dr. Schmidt',
          hospital: 'Charité Berlin',
          profile_count: 4,
          usage_count: 3,
          based_on: 'gold_cardiovascular_v2_1'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleImportProfile = async (profileId) => {
    setValidationProgress(true);
    setValidationResult(null);
    
    // Simulate validation process
    setTimeout(() => {
      setValidationResult({
        success: true,
        bundle_name: 'GOLD Oncology Bundle v2.1',
        profiles_detected: 8,
        fhir_compliant: true,
        gold_compliant: true,
        argus_compatibility: 89,
        vision_zero_ready: true,
        manual_review_needed: 2
      });
      setValidationProgress(false);
    }, 3000);
  };

  const filteredBundles = profileBundles.filter(bundle => {
    const matchesSearch = bundle.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bundle.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDomain = domainFilter === 'all' || bundle.medical_domain === domainFilter;
    return matchesSearch && matchesDomain;
  });

  const getDomainColor = (domain) => {
    const colors = {
      cardiovascular: 'error',
      oncology: 'warning', 
      epidemiology: 'info',
      custom: 'secondary'
    };
    return colors[domain] || 'default';
  };

  const handlePreview = (bundle) => {
    setSelectedBundle(bundle);
    setPreviewDialogOpen(true);
  };

  const handleUsage = (bundle) => {
    setSelectedBundle(bundle);
    setUsageDialogOpen(true);
  };

  const handleConfigure = (bundle) => {
    setSelectedBundle(bundle);
    setConfigureDialogOpen(true);
  };

  const handleExport = (bundle) => {
    // Create a downloadable JSON file
    const dataStr = JSON.stringify(bundle, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `${bundle.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const ProfileBundleCard = ({ bundle }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ConsortiumIcon color="primary" />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              {bundle.name}
            </Typography>
          </Box>
          {bundle.vision_zero_compatible && (
            <Chip label="Vision Zero" color="success" size="small" />
          )}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {bundle.standard_organization} | Validated ✅ | Used in {bundle.usage_count} studies
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 2 }}>
          {bundle.description}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`${bundle.profile_count} FHIR Profiles`} 
            size="small" 
            icon={<ProfileIcon />} 
          />
          <Chip 
            label={bundle.medical_domain} 
            size="small" 
            color={getDomainColor(bundle.medical_domain)}
          />
        </Box>

        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          Integration Status:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {bundle.integration_info?.argus_compatible && (
            <Chip label="✅ ARGUS-Compatible" size="small" color="success" variant="outlined" />
          )}
          {bundle.integration_info?.network_ready && (
            <Chip label="✅ Network Ready" size="small" color="success" variant="outlined" />
          )}
          {bundle.vision_zero_compatible && (
            <Chip label="✅ Vision Zero" size="small" color="success" variant="outlined" />
          )}
        </Box>
        
        {bundle.integration_info?.estimated_coverage && (
          <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
            Estimated Coverage: {bundle.integration_info.estimated_coverage}
          </Typography>
        )}
      </CardContent>
      
      <CardActions>
        <Button size="small" startIcon={<VisibilityIcon />} onClick={() => handlePreview(bundle)}>
          Preview
        </Button>
        <Button size="small" startIcon={<AssessmentIcon />} onClick={() => handleUsage(bundle)}>
          View Usage
        </Button>
        <Button size="small" startIcon={<SettingsIcon />} onClick={() => handleConfigure(bundle)}>
          Configure
        </Button>
        <Button size="small" startIcon={<ExportIcon />} onClick={() => handleExport(bundle)}>
          Export
        </Button>
      </CardActions>
    </Card>
  );

  const CustomTemplateCard = ({ template }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <EditIcon color="secondary" />
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            {template.name}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Created by {template.created_by} | Based on {template.based_on}
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 2 }}>
          {template.description}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Chip label={template.hospital} size="small" color="secondary" />
          <Chip label={`${template.profile_count} Profiles`} size="small" />
          <Chip label={`Used ${template.usage_count}x`} size="small" variant="outlined" />
        </Box>
      </CardContent>
      
      <CardActions>
        <Button size="small" startIcon={<EditIcon />}>
          Edit Template
        </Button>
        <Button size="small" startIcon={<DuplicateIcon />}>
          Duplicate
        </Button>
        <Button size="small" startIcon={<ShareIcon />}>
          Share with Network
        </Button>
      </CardActions>
    </Card>
  );

  const UploadDialog = () => (
    <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        📤 Upload New FHIR Profile Bundle
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Upload Method:
          </Typography>
          <RadioGroup
            value={selectedUploadMethod}
            onChange={(e) => setSelectedUploadMethod(e.target.value)}
          >
            <FormControlLabel 
              value="file_upload" 
              control={<Radio />} 
              label="📁 Upload JSON Bundle File" 
            />
            <FormControlLabel 
              value="gold_repository" 
              control={<Radio />} 
              label="🌐 Import from GOLD Repository (Demo-Auswahl)" 
            />
            <FormControlLabel 
              value="custom_template" 
              control={<Radio />} 
              label="✏️ Create Custom Template" 
            />
          </RadioGroup>
          
          {selectedUploadMethod === 'gold_repository' && (
            <Paper sx={{ mt: 2, p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom>
                🌐 GOLD Repository (Demo-Modus):
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <ValidatedIcon color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="GOLD Cardiovascular Bundle v2.1"
                    secondary="Already imported"
                  />
                </ListItem>
                <ListItem button onClick={() => handleImportProfile('gold_oncology_v2_1')}>
                  <ListItemIcon>
                    <Radio />
                  </ListItemIcon>
                  <ListItemText 
                    primary="GOLD Oncology Bundle v2.1"
                    secondary="Import available - 8 profiles, cancer research focused"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Radio />
                  </ListItemIcon>
                  <ListItemText 
                    primary="GOLD Multi-Disease Bundle v1.5"
                    secondary="Import available - 12 profiles, epidemiology focused"
                  />
                </ListItem>
              </List>
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setUploadDialogOpen(false)}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={() => handleImportProfile('selected')}
          disabled={validationProgress}
        >
          Import Selected Profiles
        </Button>
      </DialogActions>
    </Dialog>
  );

  const ValidationDialog = () => (
    <Dialog open={validationProgress || validationResult !== null} maxWidth="md" fullWidth>
      <DialogTitle>
        {validationProgress ? '⏳ FHIR Profile Validation' : '✅ FHIR Profile Validation Results'}
      </DialogTitle>
      <DialogContent>
        {validationProgress ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Validating Profile Bundle...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analyzing FHIR compliance and ARGUS compatibility
            </Typography>
          </Box>
        ) : validationResult && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Bundle: {validationResult.bundle_name} - Validation Status: ✅ All profiles valid
            </Alert>
            
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Profile Analysis:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary={`• ${validationResult.profiles_detected} FHIR profiles detected`} />
              </ListItem>
              <ListItem>
                <ListItemText primary="• All profiles conform to FHIR R4" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• GOLD standard compliance verified" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Compatible with existing ARGUS data structure" />
              </ListItem>
            </List>
            
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Integration Preview:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><ValidatedIcon color="success" /></ListItemIcon>
                <ListItemText primary={`Can map to ${validationResult.argus_compatibility}% of ARGUS oncology parameters`} />
              </ListItem>
              <ListItem>
                <ListItemIcon><ValidatedIcon color="success" /></ListItemIcon>
                <ListItemText primary="Compatible with Vision Zero network standards" />
              </ListItem>
              {validationResult.manual_review_needed > 0 && (
                <ListItem>
                  <ListItemIcon><AssessmentIcon color="warning" /></ListItemIcon>
                  <ListItemText primary={`${validationResult.manual_review_needed} profiles require manual review for custom extensions`} />
                </ListItem>
              )}
            </List>
          </Box>
        )}
      </DialogContent>
      {!validationProgress && (
        <DialogActions>
          <Button onClick={() => {
            setValidationResult(null);
            setUploadDialogOpen(false);
            // Refresh data to show new profile
            fetchProfileData();
          }}>
            Import Bundle
          </Button>
          <Button onClick={() => setValidationResult(null)}>
            Review Details
          </Button>
          <Button onClick={() => {
            setValidationResult(null);
            setUploadDialogOpen(false);
          }}>
            Cancel Import
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );

  const PreviewDialog = () => (
    <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle>
        👁️ Profile Bundle Preview: {selectedBundle?.name}
      </DialogTitle>
      <DialogContent>
        {selectedBundle && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {selectedBundle.description}
            </Alert>
            
            <Typography variant="h6" gutterBottom>Bundle Information</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2"><strong>Organization:</strong> {selectedBundle.standard_organization}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2"><strong>Version:</strong> {selectedBundle.version}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2"><strong>Domain:</strong> {selectedBundle.medical_domain}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2"><strong>Profile Count:</strong> {selectedBundle.profile_count}</Typography>
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>Profile Details</Typography>
            {selectedBundle.profiles?.map((profile, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ProfileIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {profile.name}
                  </Typography>
                  <Chip 
                    label={profile.type} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                    sx={{ ml: 2 }} 
                  />
                  {profile.required && (
                    <Chip 
                      label="Required" 
                      size="small" 
                      color="error" 
                      sx={{ ml: 1 }} 
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {profile.description}
                </Typography>
              </Paper>
            ))}

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Integration Information</Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <ValidatedIcon color={selectedBundle.integration_info?.argus_compatible ? 'success' : 'error'} />
                </ListItemIcon>
                <ListItemText 
                  primary="ARGUS Compatible" 
                  secondary={`Estimated Coverage: ${selectedBundle.integration_info?.estimated_coverage || 'Unknown'}`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <NetworkIcon color={selectedBundle.integration_info?.network_ready ? 'success' : 'error'} />
                </ListItemIcon>
                <ListItemText primary="Network Ready" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ConsortiumIcon color={selectedBundle.vision_zero_compatible ? 'success' : 'error'} />
                </ListItemIcon>
                <ListItemText primary="Vision Zero Compatible" />
              </ListItem>
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        <Button variant="contained" onClick={() => handleExport(selectedBundle)}>
          Export Bundle
        </Button>
      </DialogActions>
    </Dialog>
  );

  const UsageDialog = () => (
    <Dialog open={usageDialogOpen} onClose={() => setUsageDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        📊 Usage Statistics: {selectedBundle?.name}
      </DialogTitle>
      <DialogContent>
        {selectedBundle && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              This profile bundle is actively used in {selectedBundle.usage_count} studies across the network.
            </Alert>

            <Typography variant="h6" gutterBottom>Current Usage</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main">{selectedBundle.usage_count}</Typography>
                  <Typography variant="body2">Active Studies</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">5</Typography>
                  <Typography variant="body2">Participating Hospitals</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main">1,247</Typography>
                  <Typography variant="body2">Total Patients</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>Recent Studies</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Cardiotoxicity in Breast Cancer Patients"
                  secondary="Charité Berlin • 156 patients • Active since Jan 10, 2024"
                />
                <Chip label="Active" color="success" size="small" />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="ACE Inhibitor Effectiveness Study"
                  secondary="München Klinikum • 89 patients • Active since Dec 15, 2023"
                />
                <Chip label="Active" color="success" size="small" />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Hypertension Population Study"
                  secondary="Hamburg UKE • 234 patients • Completed Nov 20, 2023"
                />
                <Chip label="Completed" color="default" size="small" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Network Impact</Typography>
            <Typography variant="body2" color="text.secondary">
              This profile bundle contributes to the Vision Zero network by providing standardized 
              cardiovascular research data across {selectedBundle.usage_count} active studies. 
              The consistent data structure enables cross-hospital collaboration and benchmarking.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setUsageDialogOpen(false)}>Close</Button>
        <Button variant="outlined">Download Usage Report</Button>
      </DialogActions>
    </Dialog>
  );

  const ConfigureDialog = () => (
    <Dialog open={configureDialogOpen} onClose={() => setConfigureDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        ⚙️ Configure Profile Bundle: {selectedBundle?.name}
      </DialogTitle>
      <DialogContent>
        {selectedBundle && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Configuration changes will affect all studies using this profile bundle. 
              Please proceed with caution.
            </Alert>

            <Typography variant="h6" gutterBottom>Profile Selection</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which profiles should be required or optional for new studies:
            </Typography>

            {selectedBundle.profiles?.map((profile, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Radio 
                    checked={profile.required}
                    value={profile.required ? 'required' : 'optional'}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">{profile.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {profile.description}
                    </Typography>
                  </Box>
                }
                sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}
              />
            ))}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Bundle Settings</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bundle Name"
                  defaultValue={selectedBundle.name}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  defaultValue={selectedBundle.description}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Medical Domain</InputLabel>
                  <Select
                    value={selectedBundle.medical_domain}
                    label="Medical Domain"
                  >
                    <MenuItem value="cardiovascular">Cardiovascular</MenuItem>
                    <MenuItem value="oncology">Oncology</MenuItem>
                    <MenuItem value="epidemiology">Epidemiology</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Version"
                  defaultValue={selectedBundle.version}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              Configuration changes require validation and may take 24-48 hours to propagate 
              across the network.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfigureDialogOpen(false)}>Cancel</Button>
        <Button variant="outlined">Save Draft</Button>
        <Button variant="contained">Apply Changes</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <MainLayout>
      <Box sx={{ width: '100%' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              🏥 ARGUS Profile Management
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Repository Status: 3 Standard-Sets, 2 Custom Templates | Last Update: 15.01.2024 | Validation Status: All Profiles Valid ✅
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setUploadDialogOpen(true)}>
              Upload Profile
            </Button>
            <Button variant="contained" startIcon={<AddIcon />}>
              Create Bundle
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="🔍 Search profiles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Domain</InputLabel>
                <Select
                  value={domainFilter}
                  label="Domain"
                  onChange={(e) => setDomainFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="cardiovascular">Cardiovascular</MenuItem>
                  <MenuItem value="oncology">Oncology</MenuItem>
                  <MenuItem value="epidemiology">Epidemiology</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label="GOLD" size="small" clickable />
                <Chip label="HL7" size="small" clickable />
                <Chip label="Custom" size="small" clickable />
                <Chip label="Vision Zero" size="small" clickable color="success" />
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Standard Profile Bundles" />
            <Tab label="Custom Templates" />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tabValue === 0 && (
              <Grid container spacing={3}>
                {filteredBundles.map((bundle) => (
                  <Grid item xs={12} md={6} lg={4} key={bundle.id}>
                    <ProfileBundleCard bundle={bundle} />
                  </Grid>
                ))}
              </Grid>
            )}
            
            {tabValue === 1 && (
              <>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  📁 Custom Study Templates
                </Typography>
                <Grid container spacing={3}>
                  {customTemplates.map((template) => (
                    <Grid item xs={12} md={6} lg={4} key={template.id}>
                      <CustomTemplateCard template={template} />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </>
        )}

        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => setUploadDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      </Box>

      <UploadDialog />
      <ValidationDialog />
      <PreviewDialog />
      <UsageDialog />
      <ConfigureDialog />
    </MainLayout>
  );
};

export default ProfileManagementPage;